import React, { useState } from 'react';
import { useResearchGeneration } from '../../hooks/useResearchGeneration';

interface ResearchGeneratorProps {
  projectId: string;
  projectName: string;
  onGenerationStart: () => void;
}

/**
 * Component for initiating research generation
 * Provides brand/category input and handles generation trigger
 */
export const ResearchGenerator: React.FC<ResearchGeneratorProps> = ({
  projectId,
  projectName,
  onGenerationStart,
}) => {
  const [brandName, setBrandName] = useState(projectName);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const { generate, loading, error } = useResearchGeneration(projectId);

  const handleGenerate = async () => {
    if (!brandName.trim()) {
      return;
    }

    try {
      onGenerationStart();
      await generate(brandName, additionalInstructions.trim() || undefined);
    } catch (err) {
      // Error is handled by the hook
      console.error('Generation failed:', err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="max-w-2xl mx-auto">
        {/* Icon and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Generate Market Research
          </h2>
          <p className="text-gray-600">
            Our AI will conduct deep research on your brand or category, analyzing market data, competitors, and consumer trends.
          </p>
        </div>

        {/* Input Form */}
        <div className="space-y-6">
          <div>
            <label htmlFor="brand-name" className="block text-sm font-medium text-gray-700 mb-2">
              Brand or Category Name
            </label>
            <input
              id="brand-name"
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="e.g., Nike, Athletic Footwear, Tesla"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              disabled={loading}
            />
            <p className="mt-2 text-sm text-gray-500">
              Enter a specific brand name or broader category for market research
            </p>
          </div>

          <div>
            <label htmlFor="additional-instructions" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Research Instructions <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <textarea
              id="additional-instructions"
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              placeholder="e.g., Focus on Gen Z demographics in urban markets, emphasize sustainability trends, compare to Adidas and Under Armour..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
              disabled={loading}
            />
            <p className="mt-2 text-sm text-gray-500">
              Provide specific areas to focus on, demographics to emphasize, competitors to analyze, geographic markets, or any other research priorities
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-red-900 mb-1">
                    Generation Failed
                  </h4>
                  <p className="text-sm text-red-700">
                    {error.message}
                  </p>
                  <button
                    onClick={handleGenerate}
                    className="mt-3 text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  What to expect
                </h4>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>Research takes 2-4 minutes to complete</li>
                  <li>AI searches web for market intelligence</li>
                  <li>Comprehensive report with insights and trends</li>
                  <li>Report updates automatically when ready</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={loading || !brandName.trim()}
            className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Starting Research...
              </span>
            ) : (
              'Generate Research Report'
            )}
          </button>
        </div>

        {/* Features List */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Your report will include:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-700">Market overview and sizing</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-700">Competitor analysis</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-700">Consumer trends and insights</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-700">Key opportunities and risks</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
